<template>
  <div>
    <bj-add-inquiry-modal
      ref="addInquiryModal"
      @added="inquiryAdded"
    />
    <bj-match-agent-modal
      :match-an-agent="matchAnAgentModal"
      :include-unverified="false"
    />
    <div class="row full-row-exactly">
      <div class="col-12 flex-1">
        <div
          class="gutter-sm"
          :class="$q.screen.lt.md && !showFiltersMobile ? 'column' : 'row'"
        >
          <div
            v-if="$q.screen.lt.md || showFiltersDesktop"
            :class="$q.screen.lt.md && !showFiltersMobile ? 'col-auto' : 'col-md-3'"
          >
            <q-card :class="$q.screen.gt.sm ? 'full-height' : ''">
              <q-card-title>
                <div class="row">
                  <div class="col">
                    Filters
                  </div>
                  <div
                    v-if="$q.screen.lt.md"
                    class="col-auto q-mr-auto"
                  >
                    <q-btn
                      color="secondary"
                      flat
                      round
                      :icon="`keyboard_arrow_${showFiltersMobile ? 'up' :'down'}`"
                      @click="toggleFiltersMobile"
                    />
                  </div>
                </div>
              </q-card-title>
              <q-card-main
                v-if="$q.screen.gt.sm || showFiltersMobile"
                style="height: calc(100% - 64px)"
              >
                <component
                  :is="$q.screen.gt.sm ? 'q-scroll-area' : 'div'"
                  :class="{'full-height': $q.screen.gt.sm}"
                >
                  <bj-filters
                    ref="filtersModal"
                    :key="filtersResetReRenderKey"
                    v-model="filters"
                    :fields="filterFields"
                    :statuses="$config.inquiryStatuses"
                    @reset="filtersResetReRenderKey++"
                    @input="initFilters"
                  />
                </component>
              </q-card-main>
            </q-card>
          </div>
          <div class="col">
            <bj-data-table
              ref="dataTable"
              :filter-button="true"
              title="Inquiries"
              :columns="columns"
              add-button-label="Add Inquiry"
              many-name="Inquiries"
              single-name="Inquiry"
              :with-add-button="true"
              :with-cell-edit-button="false"
              :with-cell-delete-button="false"
              :with-total-count="false"
              :query-params="queryParams"
              class="full-height"
              @toggle-filters="toggleFiltersDesktop"
              @add="openAddModal"
              @fetch="fetchInquiries"
            >
              <template
                slot="cell-id"
                slot-scope="{row}"
              >
                {{ row.id }}
              </template>
              <template
                slot="cell-inquiry_source_id"
                slot-scope="{row}"
              >
                {{ row.source.name }}
              </template>
              <template
                slot="cell-entered_inquiry_at"
                slot-scope="{row}"
              >
                <span v-if="row.entered_inquiry_at">
                  <SdDatetime
                    :value="row.entered_inquiry_at"
                    :market-id="row.property && row.property.market_id"
                    :timezone="row.property && row.property.timezone"
                  />
                </span>
              </template>
              <template
                slot="cell-property"
                slot-scope="{row}"
              >
                <span v-if="row.property">
                  <router-link
                    v-if="userRoleIs($config.roles.SUPER_ADMIN, $config.roles.ADMIN)"
                    :to="{ name: 'app.properties.combined', params: { id: row.property_id } }"
                  >{{ row.property.short_address }}</router-link>
                  <span v-else>{{ row.property.short_address }}</span>
                </span>
                <span v-else>
                  {{ row.interested_in_property }}
                </span>
              </template>
              <template
                slot="cell-business"
                slot-scope="{row}"
              >
                <span v-if="row.property && row.property.business">
                  <router-link :to="{ name: 'app.businesses.profile', params: { id: row.property.business.id } }">
                    {{ row.property.business.name }}
                  </router-link>
                </span>
              </template>
              <template
                slot="cell-listing_manager"
                slot-scope="{row}"
              >
                <span v-if="row.property && row.property.listing_manager">
                  {{ row.property.listing_manager.full_name }}
                </span>
              </template>
              <template
                slot="cell-phone_number_verified_at"
                slot-scope="{row}"
              >
                <span v-if="row.phone_number_verified_at">
                  <SdDatetime
                    :value="row.phone_number_verified_at"
                    :market-id="row.property && row.property.market_id"
                    :timezone="row.property && row.property.timezone"
                  />
                </span>
              </template>
              <template
                slot="cell-lead_id"
                slot-scope="{row}"
              >
                <a
                  v-if="row.persona_identification_url"
                  :href="row.persona_identification_url"
                  target="_blank"
                  class="q-link"
                >
                  <q-item-tile icon="person" />
                </a>
                <SdLeadFullName
                  :with-link="userRoleIs($config.roles.SUPER_ADMIN, $config.roles.ADMIN)"
                  :lead="row.lead"
                />
              </template>
              <template
                slot="cell-status"
                slot-scope="{row}"
              >
                <q-chip
                  dense
                  :color="row.status === $config.inquiryStatuses.MATCHING_FAILED ? 'negative' : ''"
                >
                  {{ row.status.toUpperCase() }}
                </q-chip>
                <q-icon
                  v-if="row.uninterested_at"
                  v-tippy
                  color="negative"
                  name="cancel"
                  title="Marked as uninterested"
                />
              </template>
              <template
                slot="cell-created_at"
                slot-scope="{row}"
              >
                <SdDatetime
                  :value="row.created_at"
                  :market-id="row.property && row.property.market_id"
                  :timezone="row.property && row.property.timezone"
                />
              </template>
              <template
                slot="cell-tour_id"
                slot-scope="{row}"
              >
                <q-btn
                  v-if="row.schedule_inquiry && row.schedule_inquiry.schedule.tour"
                  rounded
                  outline
                  size="sm"
                  color="primary"
                  :label="row.schedule_inquiry.schedule.tour.id"
                  @click="redirect('app.tours', { id: row.schedule_inquiry.schedule.tour.id })"
                />
                <q-chip
                  v-if="row.schedule_inquiry && row.schedule_inquiry.schedule.is_showami"
                  class="ml-2"
                  dense
                  color="orange"
                  text-color="white"
                >
                  Showami
                </q-chip>
              </template>
              <template
                slot="cell-tour_at"
                slot-scope="{row}"
              >
                <component
                  :is="get(row,'schedule_inquiry.schedule.meeting_room.deleted_at', true) ? 'span' : 'a'"
                  v-if="get(row,'schedule_inquiry.schedule.is_video')"
                  :href="`https://${get(row,'schedule_inquiry.schedule.meeting_room.url')}`"
                  target="_blank"
                >
                  <q-chip
                    dense
                    color="blue"
                    text-color="black"
                    style="margin-right: 5px; padding: 0.25rem 0.5rem;"
                  >
                    <i class="q-icon fa fa-video" />
                  </q-chip>
                </component>
                <span
                  v-if="row.schedule_inquiry"
                  v-html="getTourAtCell(row)"
                />
              </template>
              <template
                slot="cell-city"
                slot-scope="{row}"
              >
                <span v-if="row.property && row.property.address && row.property.address.data">
                  {{ row.property.address.data.vicinity }}
                </span>
              </template>
              <template
                slot="cell-market"
                slot-scope="{row}"
              >
                <span v-if="row.property && row.property.market">
                  {{ row.property.market.name }}
                </span>
              </template>
              <template
                slot="cell-actions"
                slot-scope="{row}"
              >
                <q-btn
                  v-if="isInquiryStatusPending(row) || row.status === $config.inquiryStatuses.MATCHING_FAILED"
                  v-tippy
                  size="sm"
                  rounded
                  outline
                  color="black"
                  icon="edit"
                  title="Edit"
                  class="q-mr-sm"
                  @click="openEditModal(row)"
                />
                <q-btn
                  v-if="isInquiryStatusPending(row) && !row.uninterested_at"
                  v-tippy
                  size="sm"
                  rounded
                  outline
                  color="negative"
                  icon="cancel"
                  title="Mark as uninterested"
                  class="q-mr-sm"
                  @click="markAsUninterested(row)"
                />
                <q-btn
                  v-if="shouldShowMarkAsIdentifiedButton(row)"
                  v-tippy
                  size="sm"
                  rounded
                  outline
                  color="green"
                  icon="fingerprint"
                  title="Mark as identified"
                  class="q-mr-sm"
                  @click="markAsIdentified(row)"
                />
                <q-btn
                  v-tippy
                  :to="{ name: 'app.inquiries.timeline', params: { id: row.id }}"
                  size="sm"
                  rounded
                  outline
                  color="primary"
                  icon="timeline"
                  title="Timeline"
                  class="q-mr-sm"
                />
                <a :href="`${$config.api.baseUrl}lead/inquiry-ui/${row.uuid}?admin=1`">
                  <q-btn
                    v-if="row.status !== $config.inquiryStatuses.NEW && row.status !== $config.inquiryStatuses.MATCHING_FAILED"
                    v-tippy
                    size="sm"
                    rounded
                    outline
                    color="purple"
                    icon="chevron_right"
                    title="Enter"
                    :class="row.status === $config.inquiryStatuses.IN_PROGRESS ? 'q-mr-sm' : ''"
                  />
                </a>
                <q-btn
                  v-if="row.status === $config.inquiryStatuses.IN_PROGRESS
                    && get(row.property, 'market.showing_agents_available')!== false
                    && get(row, 'schedule_inquiry.schedule.showami_id') === null"
                  v-tippy
                  size="sm"
                  rounded
                  outline
                  color="secondary"
                  icon="link"
                  title="Match an agent"
                  @click="openMatchAnAgentModal(row)"
                />
                <!--                <q-btn-->
                <!--                  v-if="row.status === $config.inquiryStatuses.IN_PROGRESS-->
                <!--                    && get(row, 'schedule_inquiry.schedule.is_showami')"-->
                <!--                  v-tippy-->
                <!--                  size="sm"-->
                <!--                  rounded-->
                <!--                  outline-->
                <!--                  color="warning"-->
                <!--                  icon="cancel"-->
                <!--                  title="Release Showami"-->
                <!--                  @click="releaseShowami(row)"-->
                <!--                />-->
                <q-btn
                  v-if="row.scheduleConfirmationStatus === $config.inquiryScheduleConfirmationStatuses.NOT_CONFIRMED"
                  v-tippy
                  size="sm"
                  rounded
                  outline
                  color="secondary"
                  icon="done"
                  title="Confirm on behalf of the lead"
                  class="q-ml-sm"
                  @click="openConfirmScheduleModal(row)"
                />
                <q-btn
                  v-if="row.schedule_inquiry && row.schedule_inquiry.schedule.tour && row.schedule_inquiry.schedule.tour.survey_result_id"
                  v-tippy
                  :to="{ name: 'app.tours.survey-result', params: { id: row.schedule_inquiry.schedule.tour.id }}"
                  size="sm"
                  rounded
                  outline
                  color="info"
                  icon="view_list"
                  title="Survey result"
                  class="q-ml-sm"
                />
              </template>
            </bj-data-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import get from 'lodash.get';
import SdLeadFullName from '@/components/common/SdLeadFullName';
import { canAdminMarkInquiryAsIdentified } from '@/utils/InquiryUtil';
import BjFilters from '../../components/filters';
import BjAddInquiryModal from '../../components/add-inquiry-modal';
import BjMatchAgentModal from '../../plugins/bluejay/components/match-agent-modal';

export default {
  name: 'InquiriesTable',
  components: {
    BjMatchAgentModal,
    BjFilters,
    BjAddInquiryModal,
    SdLeadFullName,
  },
  data() {
    const columns = [
      {
        name: 'id',
        field: 'id',
        label: 'ID',
        sortable: true,
      },
      {
        name: 'created_at',
        field: 'created_at',
        label: 'Created At',
        sortable: true,
        date: true,
      },
      {
        name: 'inquiry_source_id',
        field: 'inquiry_source_id',
        label: 'Source',
        sortable: true,
      },
      {
        name: 'status',
        field: 'status',
        label: 'Status',
        sortable: true,
      },
      {
        name: 'entered_inquiry_at',
        field: 'entered_inquiry_at',
        label: 'Entered page at',
        sortable: true,
      },
      {
        name: 'lead_id',
        field: 'lead_id',
        label: 'Lead',
        sortable: false,
      },
      {
        name: 'property',
        field: 'property',
        label: 'Property',
        sortable: false,
      },
    ];

    if (this.userRoleIs(this.$config.roles.SUPER_ADMIN, this.$config.roles.ADMIN)) {
      columns.push({
        name: 'business',
        field: 'business',
        label: 'Business',
        sortable: false,
      }, {
        name: 'listing_manager',
        field: 'listing_manager',
        label: 'Listing Contact',
        sortable: false,
      }, {
        name: 'phone_number_verified_at',
        field: 'phone_number_verified_at',
        label: 'Phone Number Verified At',
        sortable: false,
      });
    }

    columns.push(
      {
        name: 'comments',
        field: 'comments',
        label: 'Comments',
        sortable: true,
        maxChars: 50,
      },
      {
        name: 'type',
        field: 'type',
        label: 'Type',
      },
      {
        name: 'tour_id',
        field: 'tour_id',
        label: 'Tour ID',
      },
      {
        name: 'tour_at',
        field: 'tour_at',
        label: 'Tour At',
        date: true,
      },
      {
        name: 'city',
        field: 'city',
        label: 'City',
      },
      {
        name: 'market',
        field: 'market',
        label: 'market',
      },
    );
    return {
      editModal: {
        inquiry: null,
        visible: false,
        loader: false,
      },
      columns,
      filters: {
        from_date: null,
        to_date: null,
        from_tour_date: null,
        to_tour_date: null,
        status: [],
        inquiries: this.$route.params.id ? [this.$route.params.id] : [],
        leads: [],
        properties: [],
        businesses: [],
        markets: [],
      },
      filterFields: ['date', 'tour_at_date', 'status', 'leads', 'properties', 'businesses', 'markets'],
      showFiltersMobile: false,
      showFiltersDesktop: false,
      queryParams: {
        scopes: {},
      },
      filtersResetReRenderKey: 1,
      matchAnAgentModal: {
        visible: false,
        loading: false,
        payload: {
          inquiry: null,
          query: '',
          agent: null,
        },
      },
    };
  },
  created() {
    [this.queryParams, this.filters] = this.applySortingAndFiltering(this.queryParams, this.filters);
  },
  mounted() {
    this.$root.$on('agent-matched', () => {
      this.matchAnAgentModal.visible = false;
      if (this.$refs.dataTable) {
        this.$refs.dataTable.refresh();
      }
    });
  },
  methods: {
    get,
    fetchInquiries({ queryParams, pagination }) {
      if (this.queryParams !== null && (queryParams.page != this.queryParams.page
          || queryParams.limit != this.queryParams.limit)) {
        this.queryParams.page = queryParams.page;
        this.queryParams.limit = queryParams.limit;
      }
      // ensure backend receives per_page along with limit
      queryParams.per_page = queryParams.limit;
      queryParams.from_date = queryParams.from_date ? this.moment(queryParams.from_date).tz(this.moment.tz.guess()).utc().format('YYYY-MM-DD HH:mm:ss') : null;
      queryParams.to_date = queryParams.to_date ? this.moment(queryParams.to_date).tz(this.moment.tz.guess()).utc().format('YYYY-MM-DD HH:mm:ss') : null;
      queryParams.from_tour_date = queryParams.from_tour_date ? this.moment(queryParams.from_tour_date).tz(this.moment.tz.guess()).utc().format('YYYY-MM-DD HH:mm:ss') : null;
      queryParams.to_tour_date = queryParams.to_tour_date ? this.moment(queryParams.to_tour_date).tz(this.moment.tz.guess()).utc().format('YYYY-MM-DD HH:mm:ss') : null;
      this.api().request('GET', 'admin/inquiries', {}, queryParams).then((response) => {
        this.$refs.dataTable.serverPagination = pagination;
        const hasTotal = response && response.total != null;
        this.$refs.dataTable.hasUnknownTotal = !hasTotal;
        this.$refs.dataTable.serverPagination.rowsNumber = hasTotal ? response.total : 999999999;
        if (this.$refs.dataTable.hasUnknownTotal) {
          this.$refs.dataTable.hasNextPage = !!response.next_page_url;
          this.$refs.dataTable.hasPrevPage = !!response.prev_page_url;
        }
        this.$refs.dataTable.serverData = response.data;
        this.$refs.dataTable.loading = false;
        this.addParamsToLocation(queryParams);
      }).catch((error) => {
        console.log(error);
      });
    },
    shouldShowMarkAsIdentifiedButton(inquiry) {
      return canAdminMarkInquiryAsIdentified(inquiry);
    },
    isInquiryStatusPending(inquiry) {
      return inquiry.status === this.$config.inquiryStatuses.PENDING;
    },
    initFilters() {
      Object.keys(this.filters).forEach((key) => {
        const filter = this.clone(this.filters[key]);

        if (filter && filter.length) {
          if (['status'].includes(key)) {
            this.queryParams.scopes[key] = filter;
          } else {
            this.queryParams[key] = filter;
          }
        } else if (['status'].includes(key)) {
          delete this.queryParams.scopes[key];
        } else {
          delete this.queryParams[key];
        }
      });
      setTimeout(() => {
        this.$refs.dataTable.refresh();
      }, 100);
    },
    openAddModal() {
      this.$refs.addInquiryModal.show();
    },
    inquiryAdded() {
      if (this.$refs.dataTable) {
        this.$refs.dataTable.refresh();
      }
    },
    getTourAtCell(inquiry) {
      const tourAt = inquiry.schedule_inquiry.schedule.tour_at;
      let result = `<span>${this.moment(tourAt).tz(inquiry.property.timezone).format('MM/DD/YY h:mmA')}</span>`;
      if (this.moment(tourAt).isAfter()) {
        if (inquiry.scheduleConfirmationStatus === this.$config.inquiryScheduleConfirmationStatuses.CONFIRMED) {
          result += " (<span class='text-green'>" + 'Confirmed' + '</span>)';
        } else if (inquiry.scheduleConfirmationStatus === this.$config.inquiryScheduleConfirmationStatuses.NOT_REQUIRED) {
          result += " (<span class='text-grey'>" + 'No confirmation required' + '</span>)';
        } else if (inquiry.scheduleConfirmationStatus === this.$config.inquiryScheduleConfirmationStatuses.NOT_CONFIRMED) {
          result += " (<span class='text-red'>" + 'Confirmation required' + '</span>)';
        }
      }
      return result;
    },
    openEditModal(inquiry) {
      this.$refs.addInquiryModal.show({
        id: inquiry.id,
        first_name: inquiry.lead.first_name,
        last_name: inquiry.lead.last_name,
        email: inquiry.lead.email,
        phone_number: inquiry.lead.phone_number,
        property_id: inquiry.property_id,
        property_name: get(inquiry, 'property.short_address'),
        property: {
          ...inquiry.property,
          address: get(inquiry, 'property.short_address'),
          unit_ids: inquiry.property ? inquiry.property.units.map((unit) => unit.id) : [],
        },
        unit_id: inquiry.unit_id,
        unit_name: inquiry.unit !== null && inquiry.unit.name || '',
        comments: inquiry.comments,
        inquiry_source_id: inquiry.inquiry_source_id,
      });
    },
    markAsIdentified(inquiry) {
      this.uiConfirm('Are you sure you want to mark this inquiry as identified?')
        .then(() => {
          this.api().request('PUT', `admin/inquiries/${inquiry.id}`, {
            is_identified: true,
          }).then(() => {
            this.$refs.dataTable.refresh();
            this.uiNotify('Inquiry marked as identified');
          }).catch((error) => {
            this.uiNotify(error.message || 'Error occurred', 'negative');
          });
        });
    },
    markAsUninterested(inquiry) {
      this.uiConfirm('Are you sure you want to mark this inquiry as "uninterested"? No further reminders and messages will be sent to the lead')
        .then(() => {
          this.api().request('PUT', `admin/inquiries/${inquiry.id}`, {
            is_uninterested: true,
          }).then(() => {
            this.$refs.dataTable.refresh();
            this.uiNotify('Inquiry marked as uninterested');
          }).catch((error) => {
            this.uiNotify(error.message || 'Error occurred', 'negative');
          });
        });
    },
    releaseShowami(inquiry) {
      this.uiConfirm('Are you sure you want to release Showami from this schedule? Penalties will apply if showami agent is already found and cancelled less than 2 hours before tour starts')
        .then(() => {
          this.api().request('DELETE', `admin/showami/${inquiry.schedule_inquiry.schedule_id}`).then(() => {
            this.$refs.dataTable.refresh();
            this.uiNotify('Unassigned Showami from schedule');
          }).catch((error) => {
            this.uiNotify(error.message || 'Error occurred', 'negative');
          });
        });
    },
    openMatchAnAgentModal(inquiry) {
      this.matchAnAgentModal = {
        visible: true,
        loading: false,
        payload: {
          inquiry,
          query: '',
          agent: null,
        },
      };
    },
    openConfirmScheduleModal(inquiry) {
      this.$q.dialog({
        title: 'Are you sure you want to confirm this tour schedule?',
        ok: true,
        cancel: true,
      }).then(() => {
        this.api().request('POST', `schedule-inquiries/${inquiry.schedule_inquiry.id}/confirm`).then(() => {
          this.uiNotify('Tour schedule was confirmed successfully');
          this.$refs.dataTable.refresh();
        }).catch((error) => {
          this.uiNotify(error.message || 'Error occurred', 'negative');
        });
      });
    },
    toggleFiltersMobile() {
      this.showFiltersMobile = !this.showFiltersMobile;
    },
    toggleFiltersDesktop() {
      this.showFiltersDesktop = !this.showFiltersDesktop;
    },
  },
};
</script>
